import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import {
  filterAgencyByPortalIdentityCode,
  filterAgencyContractStatus,
  filterAgencySubscriptionStatus,
} from '@functions/agency'

import { AgencyRequestOptions } from '@models/types'

const watchEntityKeys = ['agencies']

type AgencyFilters = {
  active?: string,
  assigned_provider_id?: number,
  brand_id?: number,
  brand_group_id?: number,
  contract_status?: string,
  limit?: number,
  marketing_platform?: string,
  order_by?: string,
  pricing_plan_id?: number,
  string?: string,
  subscription_status?: string,
}

type UseAgenciesOptions = {
  filters?: AgencyFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyRequestOptions,
  skipLoadedForKeysCheck?: boolean,
  sortBy?: string,
}

function useAgencies(options: UseAgenciesOptions = {}) {
  const {
    filters,
    requestOptions,
    skipLoadedForKeysCheck,
    sortBy = 'title asc',
  } = options || {

  }
  const {
    active: filterActive,
    assigned_provider_id: filterAssignedProviderId,
    brand_id: filterBrandId,
    brand_group_id: filterBrandGroupId,
    contract_status: filterContractStatus,
    limit: filterLimit,
    marketing_platform: filterMarketingPlatform,
    pricing_plan_id: filterPricingPlanId,
    string,
    subscription_status: filterSubscriptionStatus,
  } = filters || {}

  const {
    updatedEntities: { agencies: agenciesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencies, agencyPortals, contracts,
  } = entities || {}

  const filteredAgencies = useMemo(() => {
    const filtered = Object.values(agencies).filter((agency) => {
      const {
        active,
        assigned_brand_group_ids = [],
        assigned_provider_ids = [],
        brand_id,
        campaign_management_platform,
        id,
        pricing_plan_id,
        title,
      } = agency

      const matchActive = filterActive ? active?.toString() === filterActive : true
      const matchAssignedProvider = matchFilterArrayIncludes(assigned_provider_ids, filterAssignedProviderId)
      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      const matchBrandGroup = matchFilterArrayIncludes(assigned_brand_group_ids, Number(filterBrandGroupId))
      // @ts-expect-error TS2345
      const matchContractStatus = filterAgencyContractStatus(agency, contracts, filterContractStatus)
      const matchMarketingPlatform = (
        filterMarketingPlatform ? campaign_management_platform === filterMarketingPlatform : true
      )
      const matchPricingPlan = matchFilterNumber(pricing_plan_id, filterPricingPlanId)
      // @ts-expect-error TS2345
      const matchSubscriptionStatus = filterAgencySubscriptionStatus(agency, filterSubscriptionStatus)

      // String Match (ID, Portal Identity Code & Title)
      const matchAgencyId = id.toString() === string
      // @ts-expect-error TS2345
      const matchPortalIdentityCode = filterAgencyByPortalIdentityCode(agency, agencyPortals, string)
      const matchTitle = matchFilterString(title, string)
      const matchString = matchAgencyId || matchPortalIdentityCode || matchTitle

      return matchActive
        && matchAssignedProvider
        && matchBrand
        && matchBrandGroup
        && matchContractStatus
        && matchMarketingPlatform
        && matchPricingPlan
        && matchString
        && matchSubscriptionStatus
    })

    const [column, direction] = sortBy.split(' ')
    // @ts-expect-error TS2345
    return sortArrayBy(filtered, direction, column)
  }, [agenciesUpdatedAt, JSON.stringify(options)])

  const filteredAgenciesCount = filteredAgencies.length

  const loadMorePayload = useLoadMore({
    loadedCount: filteredAgenciesCount,
    limit: filterLimit,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading } = useReduxAction(
    'agencies',
    'loadAgencies',
    {
      ...filtersWithOffset,
      ...requestOptions,
      include_agency_contracts: !!filterContractStatus,
      include_agency_portals: !!string,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    {
      shouldPerformFn: () => performHttpRequests,
      skipLoadedForKeysCheck,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    hasFilteredAgencies: !!filteredAgencies.length,
    loading,
  }
}

export default useAgencies
